import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Helper function to create an error with status information
const createFetchError = (response, message) => {
  const error = new Error(message);
  error.status = response.status;
  error.statusText = response.statusText;
  error.response = response;
  return error;
};

export const useAuthenticatedApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { logout, isRefreshingToken } = useAuth();

  const makeRequest = useCallback(async (requestFn) => {
    // Get fresh token from localStorage
    let token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please login again.');
      logout();
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Execute the request function - let apiWithAuth interceptor handle token refresh
      const result = await requestFn(token);
      return result;
    } catch (error) {
      console.error('API request failed:', error);

      // Check if this is a 403 error (access denied)
      const isForbidden =
        (error.status === 403) ||
        (error.response?.status === 403);

      if (isForbidden) {
        setError('Access denied. You do not have permission to perform this action.');
        return null;
      }

      // For 401 errors, the apiWithAuth interceptor will handle token refresh automatically
      // If we reach here with a 401, it means the refresh failed and user was logged out
      const isUnauthorized =
        (error.status === 401) ||
        (error.response?.status === 401) ||
        (error.message && (error.message.includes('401') || error.message.toLowerCase().includes('unauthorized')));

      if (isUnauthorized) {
        setError('Session expired. Please login again.');
        return null;
      }

      // Handle other errors
      const errorMessage = error.response?.data?.message || error.message || 'Request failed';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Helper function for components to use with fetch
  const handleFetchResponse = useCallback(async (response) => {
    if (!response.ok) {
      const error = createFetchError(response, `HTTP ${response.status}: ${response.statusText}`);
      throw error;
    }
    return response;
  }, []);

  return {
    loading: loading || isRefreshingToken, // Show loading if either local loading or token refresh is happening
    error,
    makeRequest,
    clearError,
    handleFetchResponse
  };
};